import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { CalendarIcon, Plus, Edit, Trash2, Music, MapPin, Image as ImageIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState([]);
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Event form state
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    bar_id: "",
    event_date: null,
    end_date: null,
    image_url: "",
    price: "",
    ticket_url: ""
  });

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Wait for auth loading to complete
    if (!token) {
      // No token, redirect to login
      navigate("/login");
      return;
    }
    
    if (user && user.user_type !== "partner") {
      // User loaded but not partner
      navigate("/");
      return;
    }
    
    if (user) {
      fetchData();
    }
  }, [user, token, navigate]);

  const fetchData = async () => {
    try {
      // Fetch all bars for the dropdown
      const barsRes = await axios.get(`${API}/bars`);
      setBars(barsRes.data);
      
      // Fetch events created by this partner
      const eventsRes = await axios.get(`${API}/partner/events`, { headers: authHeaders });
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.bar_id || !eventForm.event_date) {
      toast.error("Completa los campos obligatorios: título, bar y fecha");
      return;
    }

    try {
      const payload = {
        ...eventForm,
        event_date: eventForm.event_date?.toISOString(),
        end_date: eventForm.end_date?.toISOString() || null,
        status: "approved" // Partner events are auto-approved
      };

      if (editingEvent) {
        await axios.put(`${API}/partner/events/${editingEvent.event_id}`, payload, { headers: authHeaders });
        toast.success("EVENTO ACTUALIZADO");
      } else {
        await axios.post(`${API}/partner/events`, payload, { headers: authHeaders });
        toast.success("EVENTO CREADO");
      }
      
      setShowEventDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.response?.data?.detail || "Error al guardar evento");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      bar_id: event.bar_id,
      event_date: event.event_date ? new Date(event.event_date) : null,
      end_date: event.end_date ? new Date(event.end_date) : null,
      image_url: event.image_url || "",
      price: event.price || "",
      ticket_url: event.ticket_url || ""
    });
    setShowEventDialog(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    
    try {
      await axios.delete(`${API}/partner/events/${eventId}`, { headers: authHeaders });
      toast.success("EVENTO ELIMINADO");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar evento");
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setEventForm({
      title: "",
      description: "",
      bar_id: "",
      event_date: null,
      end_date: null,
      image_url: "",
      price: "",
      ticket_url: ""
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-primary font-mono">CARGANDO...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tighter">
              PANEL <span className="text-primary">PARTNER</span>
            </h1>
            <p className="text-zinc-400 font-mono text-sm mt-1">
              Bienvenido, {user?.name}
            </p>
          </div>
          
          <Dialog open={showEventDialog} onOpenChange={(open) => {
            setShowEventDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary hover:bg-red-700 text-white font-mono"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                CREAR EVENTO
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white font-mono">
                  {editingEvent ? "EDITAR EVENTO" : "CREAR EVENTO"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Title */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">TÍTULO *</label>
                  <Input
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    className="bg-black border-zinc-800 rounded-none font-mono"
                    placeholder="Nombre del evento"
                    data-testid="event-title-input"
                  />
                </div>

                {/* Bar Selection */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">BAR/LOCAL *</label>
                  <Select 
                    value={eventForm.bar_id} 
                    onValueChange={(value) => setEventForm({ ...eventForm, bar_id: value })}
                  >
                    <SelectTrigger className="bg-black border-zinc-800 rounded-none font-mono" data-testid="event-bar-select">
                      <SelectValue placeholder="Seleccionar local" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-zinc-800 rounded-none max-h-60">
                      {bars.map((bar) => (
                        <SelectItem 
                          key={bar.bar_id} 
                          value={bar.bar_id}
                          className="font-mono text-sm"
                        >
                          {bar.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Event Date */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">FECHA Y HORA *</label>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="flex-1 justify-start bg-black border-zinc-800 rounded-none font-mono text-left"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {eventForm.event_date 
                            ? format(eventForm.event_date, "dd/MM/yyyy HH:mm", { locale: es })
                            : "Seleccionar fecha"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                        <Calendar
                          mode="single"
                          selected={eventForm.event_date}
                          onSelect={(date) => {
                            if (date) {
                              const newDate = new Date(date);
                              newDate.setHours(21, 0, 0, 0);
                              setEventForm({ ...eventForm, event_date: newDate });
                            }
                          }}
                          locale={es}
                          className="rounded-none"
                        />
                        <div className="p-3 border-t border-zinc-800">
                          <label className="text-zinc-400 font-mono text-xs">Hora:</label>
                          <Input
                            type="time"
                            value={eventForm.event_date ? format(eventForm.event_date, "HH:mm") : "21:00"}
                            onChange={(e) => {
                              const [hours, minutes] = e.target.value.split(":");
                              const newDate = eventForm.event_date ? new Date(eventForm.event_date) : new Date();
                              newDate.setHours(parseInt(hours), parseInt(minutes));
                              setEventForm({ ...eventForm, event_date: newDate });
                            }}
                            className="bg-black border-zinc-800 rounded-none font-mono mt-1"
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">DESCRIPCIÓN</label>
                  <Textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    className="bg-black border-zinc-800 rounded-none font-mono min-h-[100px]"
                    placeholder="Descripción del evento"
                  />
                </div>

                {/* Image URL */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">IMAGEN (URL)</label>
                  <Input
                    value={eventForm.image_url}
                    onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                    className="bg-black border-zinc-800 rounded-none font-mono"
                    placeholder="https://..."
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">PRECIO</label>
                  <Input
                    value={eventForm.price}
                    onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                    className="bg-black border-zinc-800 rounded-none font-mono"
                    placeholder="Gratis / 10€ / etc."
                  />
                </div>

                {/* Ticket URL */}
                <div>
                  <label className="text-zinc-400 font-mono text-xs mb-2 block">URL DE ENTRADAS</label>
                  <Input
                    value={eventForm.ticket_url}
                    onChange={(e) => setEventForm({ ...eventForm, ticket_url: e.target.value })}
                    className="bg-black border-zinc-800 rounded-none font-mono"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" className="border-zinc-700 rounded-none font-mono">
                    CANCELAR
                  </Button>
                </DialogClose>
                <Button 
                  onClick={handleCreateEvent}
                  className="bg-primary hover:bg-red-700 rounded-none font-mono"
                  data-testid="save-event-btn"
                >
                  {editingEvent ? "GUARDAR" : "CREAR"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#111] border border-zinc-800 p-6">
            <div className="text-3xl font-black text-white">{events.length}</div>
            <div className="text-zinc-500 font-mono text-xs">EVENTOS CREADOS</div>
          </div>
          <div className="bg-[#111] border border-zinc-800 p-6">
            <div className="text-3xl font-black text-primary">
              {events.filter(e => new Date(e.event_date) >= new Date()).length}
            </div>
            <div className="text-zinc-500 font-mono text-xs">PRÓXIMOS EVENTOS</div>
          </div>
          <div className="bg-[#111] border border-zinc-800 p-6">
            <div className="text-3xl font-black text-white">{bars.length}</div>
            <div className="text-zinc-500 font-mono text-xs">BARES DISPONIBLES</div>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-[#111] border border-zinc-800">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-white font-mono text-lg">MIS EVENTOS</h2>
          </div>
          
          {events.length === 0 ? (
            <div className="p-8 text-center">
              <Music className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 font-mono">No has creado ningún evento todavía</p>
              <Button 
                onClick={() => setShowEventDialog(true)}
                className="mt-4 bg-primary hover:bg-red-700 rounded-none font-mono"
              >
                CREAR PRIMER EVENTO
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {events.map((event) => {
                const bar = bars.find(b => b.bar_id === event.bar_id);
                const isPast = new Date(event.event_date) < new Date();
                
                return (
                  <div 
                    key={event.event_id} 
                    className={`p-4 flex flex-col sm:flex-row gap-4 ${isPast ? "opacity-60" : ""}`}
                  >
                    {/* Event Image */}
                    <div className="w-full sm:w-24 h-24 bg-zinc-900 shrink-0">
                      {event.image_url ? (
                        <img 
                          src={event.image_url} 
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-8 h-8 text-zinc-700" />
                        </div>
                      )}
                    </div>
                    
                    {/* Event Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold truncate">{event.title}</h3>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(new Date(event.event_date), "dd/MM/yyyy HH:mm", { locale: es })}
                        </span>
                        {bar && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {bar.name}
                          </span>
                        )}
                      </div>
                      {isPast && (
                        <span className="inline-block mt-2 text-xs font-mono text-zinc-500 bg-zinc-800 px-2 py-1">
                          PASADO
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditEvent(event)}
                        className="border-zinc-700 rounded-none"
                        data-testid={`edit-event-${event.event_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.event_id)}
                        className="border-zinc-700 rounded-none text-red-500 hover:text-red-400"
                        data-testid={`delete-event-${event.event_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
